﻿import {$$, execWithContext, get, getAndSetCookieConsents, toggleTargetClosestSelector} from "@/Utils"
import {MapStore} from "@/interfaces"
import {bindFindClosestButton, maxZoom, minZoom, stopLoading} from "@/MapModule"
import {Site} from "@/enums";

const markers = new Array<google.maps.marker.AdvancedMarkerElement>();
let iconName = "";
switch (window.config.site) {
    case Site.EuronicsEE:
    case Site.EuronicsLV:
        iconName = '/images/map/EuronicsPin.svg';
        break;
    case Site.NovastarLT:
        iconName = '/images/map/novanaut-head--circular.svg';
        break;
    case Site.ElesenLT:
        iconName = '/images/map/ElesenPin.svg';
}

let infoWindow: google.maps.InfoWindow;
let stores: readonly MapStore[];
let map: google.maps.Map;

function placeMarker(store: MapStore, panAndZoom = true, map: google.maps.Map) {
    const icon = document.createElement('img');
    
    if (window.config.site == Site.ElesenLT) {
        iconName = '/images/map/ElesenPin.svg'
    }

    icon.src = iconName;
    icon.alt = store.name;
    const marker = new google.maps.marker.AdvancedMarkerElement({
        position: store.latLng,
        map: map,
        zIndex: 50,
        content: icon,
        draggable: false
    } as google.maps.marker.AdvancedMarkerElementOptions);

    markers.push(marker);
    if (panAndZoom) {
        map.setZoom(maxZoom);
        map.panTo(store.latLng);
    }

    marker.addListener("click", () => {
        const infoContent = `
            <div class="maps__window">
                <p class="bold">${store.name}</p>
                <p><span class="icon"><i class="far fa-map-marker-alt"></i></span >${store.address}</p>
                <p><span class="icon"><i class="far fa-clock"></i></span>${store.openingTimes}</p>
            </div>
        `;

        infoWindow.setContent(infoContent);
        infoWindow.open(map, marker);
        map.panTo(store.latLng);
        map.setZoom(maxZoom);
    });
}

function removeMarkers() {
    markers.forEach((marker) => marker.map = null);
}

function positionSuccess(p: GeolocationPosition) {
    const location = new google.maps.LatLng(p.coords.latitude, p.coords.longitude);

    const closest = stores.map(s => ({
        distance: google.maps.geometry.spherical.computeDistanceBetween(location, s.latLng),
        latLng: s.latLng
    }))
        .reduce((prev, curr) => curr.distance < prev.distance ? curr : prev).latLng;

    const storeDiv = Array.from($$(".store")).find(x =>
        parseFloat(x.getAttribute("data-lat")) === closest.lat() &&
        parseFloat(x.getAttribute("data-lng")) === closest.lng());

    const btn = storeDiv.previousElementSibling as HTMLElement;
    if (!btn.parentElement.classList.contains("open")) {
        btn.click();
    }
    stopLoading();
}

/* Opening/closing store information. Does not need cookieConsent to work, so it's separated from other handlers*/
function initExpandStoreInfo() {
    const mapsListButtons = $$(".maps-list__button.expandable");
    mapsListButtons.forEach(
        (elem: Element) => elem.addEventListener("click", (e: Event) => {
            e.preventDefault();
            const button = e.currentTarget as Element;
            toggleTargetClosestSelector(button, "li", "open", false, 3);
        })
    );
    
    const storesListButtons = $$(".stores-list__button.expandable");
    storesListButtons.forEach(
        (elem: Element) => elem.addEventListener("click", (e: Event) => {
            e.preventDefault();
            const button = e.currentTarget as Element;
            toggleTargetClosestSelector(button, "li", "open", false, 3);
        })
    );
}

function bindHandlers() {
    const mapsListButtons = $$(".maps-list__button.expandable");
    mapsListButtons.forEach(
        (elem: Element) => elem.addEventListener("click", (e: Event) => {
            e.preventDefault();
            if (map) {
                const button = e.currentTarget as Element;
                const isOpen = button.parentElement.classList.contains("open");
                if (isOpen) {
                    const buttonId = button.getAttribute("data-id");
                    const store = stores.find(x => x.id === parseInt(buttonId));
                    if (markers.length > 0) {
                        removeMarkers();
                    }
                    placeMarker(store, true, map);
                } else {
                    defaultMapView();
                }
            }
        })
    );

    if (mapsListButtons.length === 1) {
        mapsListButtons[0].click();
    }

    bindFindClosestButton(positionSuccess);
}

function initMap() {
    infoWindow = new google.maps.InfoWindow();

    defaultMapView(true);
}

function defaultMapView(firstLoad = false) {
    stores = Array.from($$(".store")).map((store: HTMLElement) => ({
        id: parseInt(store.getAttribute("data-id")),
        name: store.getAttribute("data-name"),
        latLng: new google.maps.LatLng(
            parseFloat(store.getAttribute("data-lat")),
            parseFloat(store.getAttribute("data-lng"))
        ),
        address: store.getAttribute("data-address"),
        openingTimes: store.getAttribute("data-opening-times")
    })) as readonly MapStore[];

    const mapDiv = get("map");
    const centerLat = parseFloat(mapDiv.getAttribute("data-center-lat"));
    const centerLng = parseFloat(mapDiv.getAttribute("data-center-lng"));
    const zoom = parseInt(mapDiv.getAttribute("data-default-zoom"));

    if (firstLoad) {

        const options = {
            center: new google.maps.LatLng(centerLat, centerLng),
            zoom: zoom,
            minZoom: minZoom,
            maxZoom: maxZoom,
            gestureHandling: "greedy",
            mapId: "STORE_MAP"
        } as google.maps.MapOptions;

        map = new google.maps.Map(mapDiv, options);
    } else {
        map.setZoom(zoom);
        map.setCenter(new google.maps.LatLng(centerLat, centerLng))
    }

    stores.forEach(store => {
        placeMarker(store, false, map);
    });
}

export async function initGoogleMap() {
    window.googleMapsCallback = () => {
    };
    const map = get("map");
    if (map) {
        initExpandStoreInfo();
        bindHandlers();
        if (getAndSetCookieConsents().performance) {
            initMapObserver.observe(map);
        }
    } else {
        //we might just the stores list without the map, so we still need to be able to expand them
        const mapContainer = get("mapContainer");
        const storesOverlay = get("stores-overlay");
        if (mapContainer || storesOverlay) {
            initExpandStoreInfo();
        }
    }
}

const initMapObserver = new IntersectionObserver(entries => {
    if (entries.some((e) => e.isIntersecting)) {
        execWithContext(window.config.googleMapsApiUrl + "&callback=googleMapsCallback", () => {
            initMap();
            entries.forEach(e => {
                initMapObserver.unobserve(e.target);
            });
        });
    }
}, {threshold: [0]})